/**
 * THROWAWAY DEMO CODE (Replenishment demo). Plain-language summaries of one
 * demand item, used by the Demand Radar preview and the item page's side rail:
 * why it is flagged, how the options compare on timing, and the recommended
 * action. Everything is derived from the item's own numbers.
 */
import type { ReactNode } from 'react';
import { CheckCircleIcon } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';
import { fmtMoney } from 'src/demo/procurement/data/format';
import { available, restockQty, type DemandItem } from '../data';
import { useFulfillStatuses } from '../fulfillStore';
import type { FlowModel } from './flow-data';

function days(n: number): string {
  return `${n} day${n === 1 ? '' : 's'}`;
}

/** "SO #48255 · Sunbelt Mechanical" reads as "Order #48255 from Sunbelt Mechanical". */
function orderLabel(sourceRef: string): string {
  const match = /^SO (#\S+) · (.+)$/.exec(sourceRef);
  return match ? `Order ${match[1]} from ${match[2]}` : sourceRef;
}

export function whyFlagged(item: DemandItem): string {
  const stock = `${item.warehouse} has ${item.onHand} on hand and ${item.reserved} already promised`;
  if (item.demandSource === 'sales-order') {
    return `${orderLabel(item.sourceRef)} is due in ${days(item.needByDays)}. ${stock}, which leaves it ${item.qtyShort} ${unitWord(item.uom)} short.`;
  }
  return `Forecast demand passes what ${item.warehouse} can cover in ${days(item.needByDays)}. ${item.onHand} on hand with ${item.reserved} promised leaves ${available(item)}, against a safety buffer of ${item.safetyStock}.`;
}

export function recommendationFor(item: DemandItem): {
  title: string;
  body: string;
} {
  const transfer = item.scenarios.transfer;
  const buy = item.scenarios.buy;
  if (item.recommended === 'transfer' && transfer) {
    return {
      title: `Move ${transfer.transferUnits} from ${transfer.fromWarehouse}`,
      body: `${transfer.fromWarehouse} has ${transfer.surplusUnits} on hand, ${transfer.overMaxUnits} over its maximum. They arrive in ${days(transfer.etaDays)} instead of the ${days(transfer.vendorLeadDays)} a supplier needs, with no purchase cost.`,
    };
  }
  if (item.sourcing) {
    const prices = item.sourcing.vendors.map((v) => v.listPrice);
    return {
      title: `Compare ${item.sourcing.vendors.length} suppliers before buying ${restockQty(item)}`,
      body: `${item.sourcing.vendors.length} distributors stock it, with list prices from ${fmtMoney(Math.min(...prices), { cents: true })} to ${fmtMoney(Math.max(...prices), { cents: true })}. The AI agent can call each one, negotiate and rank them by delivered price.`,
    };
  }
  if (buy) {
    const bundleValue = buy.bundle.units * buy.bundle.unitCost;
    const total = buy.baseValue + bundleValue;
    const freight =
      total >= buy.freeFreightThreshold
        ? `and avoids ${fmtMoney(buy.freightIfBelow)} in freight`
        : `so a small order does not pay ${fmtMoney(buy.freightIfBelow)} freight on its own`;
    return {
      title: `Buy ${buy.units} from ${buy.vendor}, together with ${buy.bundle.name}`,
      body: `Delivery takes ${days(buy.leadDays)}. Ordering ${buy.bundle.units} ${buy.bundle.name} at the same time (due for reorder in ${days(buy.bundle.dueInDays)} anyway) takes the order from ${fmtMoney(buy.baseValue)} to ${fmtMoney(total)} ${freight}.`,
    };
  }
  return {
    title: 'Review the options',
    body: 'Open the item to compare ways to fill it.',
  };
}

/** What the recommendation reads once the order or transfer has been sent. */
export function doneRecommendationFor(item: DemandItem): {
  title: string;
  body: string;
} {
  const transfer = item.scenarios.transfer;
  const buy = item.scenarios.buy;
  if (item.recommended === 'transfer' && transfer) {
    return {
      title: `${transfer.transferUnits} ${unitWord(item.uom)} are on the way from ${transfer.fromWarehouse}`,
      body: `They arrive in ${days(transfer.etaDays)}, which meets the ${days(item.needByDays)} deadline. No purchase order was needed.`,
    };
  }
  if (buy) {
    return {
      title: `Order sent to ${buy.vendor}`,
      body: `${buy.units} ${unitWord(item.uom)} together with ${buy.bundle.name} are on order and arrive in ${days(buy.leadDays)}.`,
    };
  }
  return { title: 'Order sent', body: 'The order is on its way.' };
}

interface ItemRecommendationProps {
  item: DemandItem;
  actions?: ReactNode;
  /** Extra detail under the sentence, e.g. the timing bars. */
  children?: ReactNode;
  className?: string;
}

export function ItemRecommendation({
  item,
  actions,
  children,
  className,
}: ItemRecommendationProps) {
  const fulfilled = useFulfillStatuses()[item.id] === 'fulfilled';
  const { title, body } = fulfilled
    ? doneRecommendationFor(item)
    : recommendationFor(item);
  return (
    <section
      aria-label="Recommended action"
      className={cn('rounded-xl border bg-muted/40 p-3', className)}
    >
      <h3 className="flex items-start gap-2 text-sm font-semibold leading-5 text-foreground">
        {fulfilled ? (
          <CheckCircleIcon
            weight="fill"
            aria-hidden="true"
            className="mt-0.5 size-4 shrink-0 text-emerald-600"
          />
        ) : null}
        <span>{title}</span>
      </h3>
      <div className="mt-1.5 text-sm text-foreground/80">
        <p>{body}</p>
        {children ? <div className="mt-3">{children}</div> : null}
      </div>
      {actions ? (
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          {actions}
        </div>
      ) : null}
    </section>
  );
}

interface TimingRow {
  label: string;
  days: number;
  kind: 'need' | 'option';
}

export function ItemTiming({ item }: { item: DemandItem }) {
  const transfer = item.scenarios.transfer;
  const supplierDays = item.scenarios.buy?.leadDays ?? item.leadTimeDays;
  const rows: TimingRow[] = [
    { label: 'Needed by', days: item.needByDays, kind: 'need' },
    { label: 'Supplier delivery', days: supplierDays, kind: 'option' },
  ];
  if (transfer) {
    rows.push({
      label: `Transfer from ${transfer.fromWarehouse}`,
      days: transfer.etaDays,
      kind: 'option',
    });
  }
  const longest = Math.max(...rows.map((r) => r.days), 1);
  return (
    <ul className="space-y-2">
      {rows.map((row) => {
        const onTime = row.days <= item.needByDays;
        return (
          <li key={row.label} className="text-sm">
            <div className="flex items-baseline justify-between gap-3">
              <span>{row.label}</span>
              <span
                className={cn(
                  'text-xs tabular-nums',
                  row.kind === 'need'
                    ? 'text-muted-foreground'
                    : onTime
                      ? 'text-emerald-700'
                      : 'text-red-700',
                )}
              >
                {days(row.days)}
                {row.kind === 'option'
                  ? onTime
                    ? ' · in time'
                    : ' · too late'
                  : ''}
              </span>
            </div>
            <div className="mt-1 h-1.5 rounded-full bg-muted">
              <div
                className={cn(
                  'h-full rounded-full',
                  row.kind === 'need'
                    ? 'bg-slate-400'
                    : onTime
                      ? 'bg-emerald-600'
                      : 'bg-red-500',
                )}
                style={{ width: `${Math.max(4, (row.days / longest) * 100)}%` }}
              />
            </div>
          </li>
        );
      })}
    </ul>
  );
}

const MIX_TONES = ['bg-slate-700', 'bg-slate-500', 'bg-slate-300'];

export function DemandMix({ model }: { model: FlowModel }) {
  const total = model.bracketMix.reduce((sum, m) => sum + m.units, 0);
  return (
    <div>
      <div
        role="img"
        aria-label="Share of weekly bracket demand by use"
        className="flex h-2 overflow-hidden rounded-full bg-muted"
      >
        {model.bracketMix.map((m, i) => (
          <div
            key={m.label}
            className={MIX_TONES[i % MIX_TONES.length]}
            style={{ width: `${(m.units / total) * 100}%` }}
          />
        ))}
      </div>
      <ul className="mt-2 space-y-1 text-sm">
        {model.bracketMix.map((m, i) => (
          <li key={m.label} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className={cn(
                  'size-2 rounded-sm',
                  MIX_TONES[i % MIX_TONES.length],
                )}
              />
              {m.label}
            </span>
            <span className="text-xs tabular-nums text-muted-foreground">
              {m.units} a week · {Math.round((m.units / total) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Plain word for a unit of measure code. */
export function unitWord(uom: string): string {
  if (uom === 'EA') return 'units';
  if (uom === 'RL') return 'rolls';
  return uom.toLowerCase();
}
